import React, { useState, useContext, useEffect } from 'react';
import { AccountContext } from '../../../components/AccountContext';
import { Panel } from 'primereact/panel';
import { defineMessages, useIntl } from 'react-intl';
import gql from 'graphql-tag';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { Loading } from '../../../components/Loading';
import { growlErrors } from '../../../utils/NotificationUtil';
import { useQuery } from '@apollo/react-hooks';
import AgreedCommission from './AgreedCommission';
import UnsettledCommissionNotice from './UnsettledCommissionNotice';

const AGREED_COMMISSIONS_QUERY = gql`
  query agreedCommissionsQuery($accountId: ID!) {
    account(id: $accountId) {
      gpiPartnersWithAgreedCommissions {
        id
        name
        creditCommissionPercentage
        noLimitCommissionPercentage
        superCreditsCommissionPercentage
      }
    }
  }
`;

const i18nMessages = defineMessages({
  agreedCommission: {
    id: 'account.agreed-commissions',
    defaultMessage: 'Agreed commissions',
  },
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  id: { id: 'attrs.id', defaultMessage: 'ID' },
  credit: { id: 'account.agreed-commissions.credits', defaultMessage: 'Credits' },
  noLimit: { id: 'account.agreed-commissions.no-limit', defaultMessage: 'NoLimit' },
  superMatch: { id: 'account.agreed-commissions.super-match', defaultMessage: 'SuperMatch' },
  noPartners: {
    id: 'account.agreed-commissions.no-partners',
    defaultMessage: 'There are currently no partners with commissions',
  },
});

const AgreedCommissions = () => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const [partnersWithCommissions, setPartnersWithCommissions] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);

  const { loading, error, data } = useQuery(AGREED_COMMISSIONS_QUERY, {
    variables: {
      accountId: currentAccount,
    },
    fetchPolicy: 'no-cache',
  });

  const { account } = data;

  useEffect(() => {
    if (account) {
      setPartnersWithCommissions(account.gpiPartnersWithAgreedCommissions);
    }
    // eslint-disable-next-line
  }, [account]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const handleShowDialog = (name, unsettledCommissionType) => {
    setSelectedPartner({
      name,
      unsettledCommissionType,
    });
  };

  const handleCloseDialog = () => setSelectedPartner(null);

  return (
    <>
      <UnsettledCommissionNotice selectedPartner={selectedPartner} closeDialog={handleCloseDialog} />
      <div className="p-grid">
        <div className="p-col-12">
          <div className="brand-detailed-wrapper">
            <Panel header={formatMessage(i18nMessages.agreedCommission)}>
              {partnersWithCommissions.length === 0 && (
                <div className="align-center font-weight-600">{formatMessage(i18nMessages.noPartners)}</div>
              )}
              {partnersWithCommissions.length > 0 && (
                <>
                  <div className="font-weight-600 flex-space-between margin-bottom-0-5">
                    <div>{formatMessage(i18nMessages.name)}</div>
                    <div>{formatMessage(i18nMessages.id)}</div>
                    <div>{formatMessage(i18nMessages.credit)}</div>
                    <div>{formatMessage(i18nMessages.noLimit)}</div>
                    <div></div>
                  </div>
                  <>
                    {partnersWithCommissions.map((partner) => {
                      return (
                        <AgreedCommission key={partner.id} partner={partner} handleShowDialog={handleShowDialog} />
                      );
                    })}
                  </>
                </>
              )}
            </Panel>
          </div>
        </div>
      </div>
    </>
  );
};

export default AgreedCommissions;
